import { DOCUMENT } from '@angular/common';
import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, inject, Injectable } from '@angular/core';
import { Observable, Subscriber, Subscription, take } from 'rxjs';
import { SzSuccessOverlayComponent } from './sz-success-overlay.component';
import { SzSuccessOverlayDuringOptions, SzSuccessOverlayOptions } from './sz-success-overlay.types';

const CLOSE_ANIMATION_MS: number = 280;

@Injectable({ providedIn: 'root' })
export class SzSuccessOverlayService {
  private readonly appRef: ApplicationRef = inject(ApplicationRef);
  private readonly envInjector: EnvironmentInjector = inject(EnvironmentInjector);
  private readonly document: Document = inject(DOCUMENT);

  show$(opts: SzSuccessOverlayOptions = {}): Observable<void> {
    return new Observable<void>((subscriber: Subscriber<void>): (() => void) => {
      const ref: ComponentRef<SzSuccessOverlayComponent> = createComponent(SzSuccessOverlayComponent, {
        environmentInjector: this.envInjector
      });

      this.applyInputs(ref, opts);
      this.appRef.attachView(ref.hostView);
      this.document.body.appendChild(ref.location.nativeElement);

      Promise.resolve().then((): void => ref.setInput('open', true));

      let destroyed: boolean = false;
      const teardown = (): void => {
        if (destroyed) return;
        destroyed = true;
        this.appRef.detachView(ref.hostView);
        ref.destroy();
      };

      const dismissSub: Subscription = ref.instance.dismiss.subscribe((): void => {
        ref.setInput('open', false);
        setTimeout((): void => {
          subscriber.next();
          subscriber.complete();
          teardown();
        }, CLOSE_ANIMATION_MS);
      });

      return (): void => {
        dismissSub.unsubscribe();
        teardown();
      };
    });
  }

  /**
   * Pokazuje overlay w fazie `loading` na czas trwania `work$`. Po sukcesie przełącza
   * w `success` (pierścień → ptaszek) i po auto-dismiss emituje wynik `work$` oraz
   * kończy strumień. Błąd `work$` zamyka overlay i propaguje się do subskrybenta.
   */
  showDuring$<T>(work$: Observable<T>, opts: SzSuccessOverlayDuringOptions = {}): Observable<T> {
    return new Observable<T>((subscriber: Subscriber<T>): (() => void) => {
      const ref: ComponentRef<SzSuccessOverlayComponent> = createComponent(SzSuccessOverlayComponent, {
        environmentInjector: this.envInjector
      });

      if (opts.subtitle !== undefined) ref.setInput('subtitle', opts.subtitle);
      if (opts.autoDismissMs !== undefined) ref.setInput('autoDismissMs', opts.autoDismissMs);
      ref.setInput('title', opts.loadingTitle ?? '');
      ref.setInput('phase', 'loading');

      this.appRef.attachView(ref.hostView);
      this.document.body.appendChild(ref.location.nativeElement);

      Promise.resolve().then((): void => ref.setInput('open', true));

      let result: T;
      let hasResult: boolean = false;
      let destroyed: boolean = false;
      const teardown = (): void => {
        if (destroyed) return;
        destroyed = true;
        this.appRef.detachView(ref.hostView);
        ref.destroy();
      };

      // Auto-dismiss odpala się dopiero w fazie 'success' (patrz komponent).
      const dismissSub: Subscription = ref.instance.dismiss.subscribe((): void => {
        ref.setInput('open', false);
        setTimeout((): void => {
          if (hasResult) subscriber.next(result);
          subscriber.complete();
          teardown();
        }, CLOSE_ANIMATION_MS);
      });

      const workSub: Subscription = work$.pipe(take(1)).subscribe({
        next: (value: T): void => {
          result = value;
          hasResult = true;
          ref.setInput('title', opts.successTitle ?? '');
          ref.setInput('phase', 'success');
          opts.onSuccess?.();
        },
        error: (err: unknown): void => {
          ref.setInput('open', false);
          setTimeout((): void => teardown(), CLOSE_ANIMATION_MS);
          subscriber.error(err);
        }
      });

      return (): void => {
        dismissSub.unsubscribe();
        workSub.unsubscribe();
        teardown();
      };
    });
  }

  private applyInputs(ref: ComponentRef<SzSuccessOverlayComponent>, opts: SzSuccessOverlayOptions): void {
    if (opts.title !== undefined) ref.setInput('title', opts.title);
    if (opts.subtitle !== undefined) ref.setInput('subtitle', opts.subtitle);
    if (opts.autoDismissMs !== undefined) ref.setInput('autoDismissMs', opts.autoDismissMs);
  }
}
